import { useQuery } from "@tanstack/react-query";
import { SectionHeading } from "@/components/bespoke/section-heading";
import { BookOpen, ExternalLink } from "lucide-react";
import { Link } from "react-router";
import { PageHeader } from "@/components/bespoke";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { zotCommandCatalogueQuery } from "@/lib/api";
import { ZOT_SLASH_COMMANDS } from "@/lib/zot-commands";
import { zotCommandGuideContent } from "./zot-command-guide-content";

const zotDocsBase = "https://github.com/zworks-ai/zworks/blob/main/";

/** In-product index for the Zot command references served by the hub catalogue. */
export function HelpPage() {
  const catalogue = useQuery(zotCommandCatalogueQuery);
  const commands = Object.entries(zotCommandGuideContent).map(([command, guide]) => {
    const catalogueEntry = catalogue.data?.commands.find((entry) => entry.command === command);
    const shortDescription = ZOT_SLASH_COMMANDS.find((item) => item.command === command)?.purpose;
    return {
      command,
      guide,
      title: catalogueEntry?.title ?? shortDescription ?? command,
      summary: catalogueEntry?.summary ?? guide.reads,
      doc: catalogueEntry?.doc ?? `docs/zot/commands/${command.slice(1)}.md`,
    };
  });

  return (
    <main className="flex flex-col gap-6">
      <PageHeader
        title="Help and guides"
        description="Zot commands and examples from the command reference."
      />

      {catalogue.isPending ? <p role="status">Loading catalogue details…</p> : null}
      {catalogue.isError ? (
        <section role="alert" className="rounded-[var(--radius-card)] border p-4">
          {/* A transient status is not a section. As an h2 at 14 px this sat in the outline as
              a sibling of "Zot command guides" at 16 px, promising a section where there was a
              message — and lighter than the section it introduced. */}
          <p className="font-medium">Live catalogue unavailable</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Showing the in-product Zot guides. Catalogue details can be retried when the hub is
            available.
          </p>
          <Button className="mt-3" variant="outline" onClick={() => void catalogue.refetch()}>
            Try again
          </Button>
        </section>
      ) : null}

      <section aria-labelledby="zot-command-guides" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <SectionHeading id="zot-command-guides">Zot command guides</SectionHeading>
          <span className="text-sm text-muted-foreground">{commands.length} guides</span>
        </div>
        {/* Ten viewports of guides at 390 px with no way to jump. This is an index, not a
            replacement for the cards: the page stays scannable without hiding content behind
            an accordion that a screen reader would then have to expand twelve times. */}
        <nav aria-label="Jump to a command guide">
          <ul className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
            {commands.map((item) => (
              <li key={item.command}>
                <a
                  className="inline-flex min-h-11 min-w-11 items-center justify-center text-accent-text underline-offset-4 hover:underline md:min-h-0 md:min-w-0"
                  href={`#zot-guide-${item.command.slice(1)}`}
                >
                  {item.command}
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {commands.map((item) => {
            const guide = item.guide;
            return (
              <li key={item.command}>
                <Card id={`zot-guide-${item.command.slice(1)}`} className="h-full">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BookOpen aria-hidden className="size-4 shrink-0" />
                      <span>
                        {item.command} · {item.title}
                      </span>
                    </CardTitle>
                    <CardDescription>{item.summary}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-4">
                    {guide ? (
                      <>
                        <section>
                          <h3 className="text-sm font-semibold">What it reads</h3>
                          <p className="mt-1 text-sm text-muted-foreground">{guide.reads}</p>
                        </section>
                        <section>
                          <h3 className="text-sm font-semibold">What it can propose</h3>
                          {guide.canPropose.length ? (
                            <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                              {guide.canPropose.map((item) => (
                                <li key={item}>{item}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="mt-1 text-sm text-muted-foreground">
                              Nothing; this command is read-only.
                            </p>
                          )}
                        </section>
                        <section>
                          <h3 className="text-sm font-semibold">What it never does</h3>
                          <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                            {guide.never.map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        </section>
                        <section>
                          <h3 className="text-sm font-semibold">Examples</h3>
                          <ul className="mt-1 space-y-2">
                            {guide.examples.map((example) => (
                              <li key={example}>
                                <pre className="whitespace-pre-wrap rounded-md bg-muted p-2 text-xs">
                                  {example}
                                </pre>
                              </li>
                            ))}
                          </ul>
                        </section>
                      </>
                    ) : (
                      <p role="alert" className="text-sm text-danger">
                        The in-product guide for this command is not available.
                      </p>
                    )}
                    <a
                      // This stands on its own line rather than inside a sentence, so it is a
                      // target in its own right and has to clear the 44 px mobile minimum.
                      className="inline-flex min-h-11 items-center text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground md:min-h-0"
                      href={`${zotDocsBase}${item.doc}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Source document in the Zworks repository{" "}
                      <ExternalLink aria-hidden className="ml-1 inline size-3.5" />
                    </a>
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      </section>

      <p className="text-sm text-muted-foreground">
        Need help in Zot? Open{" "}
        <Link className="underline underline-offset-4" to="/zot">
          Zot
        </Link>{" "}
        and enter <code>/help</code>.
      </p>
    </main>
  );
}
